// client/src/utils/userStorage.ts (ST-00230, PLT-20)
//
// Whose browser memories the shell reads and writes. The open tab set (utils/tabSession) and
// the "resume where you left off" page (hooks/useLastVisitedRoute) live in localStorage, which
// a shared weighbridge PC shares between everyone who signs in on it: the next person got the
// previous user's tabs back and was offered their last page. Both are now kept per user
// (`<key>:<userId>`), nothing is read or written while nobody is signed in, and logout clears
// the signed-in user's (context/AuthContext).
//
// AuthContext names the user (setStorageUser) as it learns who signed in, before the shell
// renders, so the tab host's first read already sees it. With sign-in switched off (local
// development) everything belongs to one local owner, as before.
import { isAuthEnabled } from '../config/authConfig';

let currentUser: string | null = null;

/** The signed-in user (null when nobody is). */
export function setStorageUser(userId: string | number | null | undefined): void {
  currentUser = userId === null || userId === undefined || userId === '' ? null : String(userId);
}

/** `base:<userId>`, or null while nobody is signed in (then nothing is read or written). */
export function userStorageKey(base: string): string | null {
  const owner = currentUser ?? (isAuthEnabled() ? null : 'local');
  return owner ? `${base}:${owner}` : null;
}

/** localStorage access that never throws (private mode, storage full or blocked). */
export const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Losing a convenience is not worth an error.
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};
